import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import ReviewDialog from "@/components/ReviewDialog";
import { Button } from "@/components/ui/button";
import { FileText, RefreshCcw, Clock } from "lucide-react";
import WorkerAvatar from "@/components/WorkerAvatar";

export default function ReviewQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/review-queue");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppLayout>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps text-slate-500">Review queue</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            {items.length} document{items.length !== 1 && "s"} awaiting review
          </h1>
          <p className="text-slate-600 mt-1.5">Approve or reject with a clear reason. Workers see rejections instantly.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="review-reload">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="mt-8 space-y-3">
        {loading && <div className="text-slate-500 py-8">Loading queue…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <div className="font-heading text-lg font-semibold text-slate-900">All caught up</div>
            <div className="text-sm text-slate-600 mt-1">No documents currently need review.</div>
          </div>
        )}
        {!loading &&
          items.map((doc) => (
            <div
              key={doc.id}
              data-testid={`review-row-${doc.id}`}
              className="rounded-lg border border-slate-200 bg-white p-5 flex flex-wrap items-center justify-between gap-4 card-lift"
            >
              <div className="flex items-start gap-4 min-w-0">
                <WorkerAvatar workerId={doc.worker_id} name={doc.worker_name} size="md" />
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-slate-900 truncate">
                    {doc.item_title}
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5 truncate">
                    {doc.worker_name} · {doc.worker_role_label}
                  </div>
                  <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap">
                    <Clock className="h-3 w-3" />
                    {new Date(doc.created_at).toLocaleString()}
                    <span>·</span>
                    <span className="truncate max-w-xs">{doc.original_filename}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-800 border-amber-200">
                  In review
                </span>
                <Button
                  onClick={() => setActive(doc)}
                  data-testid={`open-review-${doc.id}`}
                  className="bg-slate-900 hover:bg-slate-800 rounded-full"
                >
                  Review
                </Button>
              </div>
            </div>
          ))}
      </div>

      <ReviewDialog
        open={!!active}
        onOpenChange={(v) => !v && setActive(null)}
        document={active}
        onReviewed={load}
      />
    </AppLayout>
  );
}
